import { simpleHash } from "@tamagui/helpers";
import { getConfig } from "../config";
import { defaultOffset } from "./defaultOffset";
import { normalizeValueWithProperty } from "./normalizeValueWithProperty";
import { pseudoDescriptors } from "./pseudoDescriptors";
const pseudosOrdered = [
  pseudoDescriptors.hoverStyle,
  pseudoDescriptors.pressStyle,
  pseudoDescriptors.focusStyle
];
function getStylesAtomic(stylesIn) {
  if (!(stylesIn.hoverStyle || stylesIn.pressStyle || stylesIn.focusStyle)) {
    return generateAtomicStyles(stylesIn);
  }
  const { hoverStyle, pressStyle, focusStyle, ...base } = stylesIn;
  let res = [];
  for (const [index, style] of [hoverStyle, pressStyle, focusStyle, base].entries()) {
    if (!style)
      continue;
    const pseudo = pseudosOrdered[index];
    res = [...res, ...generateAtomicStyles(style, pseudo)];
  }
  return res;
}
let conf;
const generateAtomicStyles = (styleIn, pseudo) => {
  if (!styleIn)
    return [];
  conf = conf || getConfig();
  const style = styleIn;
  if ("transform" in style && Array.isArray(style.transform)) {
    style.transform = style.transform.map(
      // { scale: 2 } => 'scale(2)'
      // { translateX: 20 } => 'translateX(20px)'
      // { matrix: [1,2,3,4,5,6] } => 'matrix(1,2,3,4,5,6)'
      (transform) => {
        const type = Object.keys(transform)[0];
        const value = transform[type];
        if (type === "matrix" || type === "matrix3d") {
          return `${type}(${value.join(",")})`;
        }
        return `${type}(${normalizeValueWithProperty(value, type)})`;
      }
    ).join(" ");
  }
  styleToCSS(style);
  const out = [];
  for (const key in style) {
    const value = normalizeValueWithProperty(style[key], key);
    if (value == null || value == void 0)
      continue;
    const hash = simpleHash(`${value}`);
    const pseudoPrefix = pseudo ? `0${pseudo.name}-` : "";
    const shortProp = conf.inverseShorthands[key] || key;
    const identifier = `_${shortProp}-${pseudoPrefix}${hash}`;
    const rules = createAtomicRules(identifier, key, value, pseudo);
    const styleObject = {
      property: key,
      pseudo: pseudo == null ? void 0 : pseudo.name,
      identifier,
      rules
    };
    if (process.env.NODE_ENV === "test") {
      styleObject.value = value;
    }
    out.push(styleObject);
  }
  return out;
};
const presetHashes = {
  none: true
};
function styleToCSS(style) {
  const { shadowOffset, shadowRadius, shadowColor } = style;
  if (style.shadowRadius) {
    const offset = shadowOffset || defaultOffset;
    const shadow = `${normalizeValueWithProperty(
      offset.width
    )} ${normalizeValueWithProperty(offset.height)} ${normalizeValueWithProperty(
      shadowRadius
    )} ${shadowColor}`;
    style.boxShadow = style.boxShadow ? `${style.boxShadow}, ${shadow}` : shadow;
    style.shadowOffset = void 0;
    style.shadowRadius = void 0;
    style.shadowColor = void 0;
  }
  const { textShadowColor, textShadowOffset, textShadowRadius } = style;
  if (textShadowColor || textShadowOffset || textShadowRadius) {
    const { height, width } = textShadowOffset || defaultOffset;
    const radius = textShadowRadius || 0;
    const color = normalizeValueWithProperty(textShadowColor, "textShadowColor");
    if (color && (height !== 0 || width !== 0 || radius !== 0)) {
      const blurRadius = normalizeValueWithProperty(radius);
      const offsetX = normalizeValueWithProperty(width);
      const offsetY = normalizeValueWithProperty(height);
      style.textShadow = `${offsetX} ${offsetY} ${blurRadius} ${color}`;
    }
    style.textShadowColor = void 0;
    style.textShadowOffset = void 0;
    style.textShadowRadius = void 0;
  }
}
function createDeclarationBlock(style, important = false) {
  let next = "";
  for (const [key, value] of style) {
    const prop = hyphenateStyleName(key);
    next += `${prop}:${value}${important ? " !important" : ""};`;
  }
  return `{${next}}`;
}
const hcache = {};
const toHyphenLower = (match) => `-${match.toLowerCase()}`;
const hyphenateStyleName = (key) => {
  if (key in hcache)
    return hcache[key];
  const val = key.replace(/[A-Z]/g, toHyphenLower);
  hcache[key] = val;
  return val;
};
const pseudoSelectorPrefixes = (() => {
  const res = {};
  for (const key in pseudoDescriptors) {
    const pseudo = pseudoDescriptors[key];
    res[pseudo.name] = `${[...Array(pseudo.priority)].map(() => ":root").join("")} `;
  }
  return res;
})();
function createAtomicRules(identifier, property, value, pseudo) {
  const selector = pseudo ? `${pseudoSelectorPrefixes[pseudo.name]} .${identifier}:${pseudo.name}` : `.${identifier}`;
  const important = !!pseudo;
  let rules = [];
  switch (property) {
    case "placeholderTextColor": {
      const block = createDeclarationBlock(
        [
          ["color", value],
          ["opacity", 1]
        ],
        important
      );
      rules.push(`${selector}::placeholder${block}`);
      break;
    }
    case "backgroundClip":
    case "userSelect": {
      const propertyCapitalized = `${property[0].toUpperCase()}${property.slice(1)}`;
      const webkitProperty = `Webkit${propertyCapitalized}`;
      const block = createDeclarationBlock(
        [
          [property, value],
          [webkitProperty, value]
        ],
        important
      );
      rules.push(`${selector}${block}`);
      break;
    }
    case "pointerEvents": {
      let finalValue = value;
      if (value === "auto" || value === "box-only") {
        finalValue = "auto";
        if (value === "box-only") {
          rules.push(`${selector}>*${boxOnly}`);
        }
      } else if (value === "none" || value === "box-none") {
        finalValue = "none";
        if (value === "box-none") {
          rules.push(`${selector}>*${boxNone}`);
        }
      }
      const block = createDeclarationBlock([["pointerEvents", finalValue]], true);
      rules.push(`${selector}${block}`);
      break;
    }
    default: {
      const block = createDeclarationBlock([[property, value]], important);
      rules.push(`${selector}${block}`);
      break;
    }
  }
  if (pseudo && pseudo.name === "hover") {
    rules = rules.map((r) => `@media not all and (hover: none) { ${r} }`);
  }
  return rules;
}
const boxNone = createDeclarationBlock([["pointerEvents", "auto"]], true);
const boxOnly = createDeclarationBlock([["pointerEvents", "none"]], true);
export {
  generateAtomicStyles,
  getStylesAtomic,
  styleToCSS
};
//# sourceMappingURL=getStylesAtomic.js.map
